import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { Droplet, CheckCircle, X, CloudRain, AlertTriangle } from 'lucide-react';

/**
 * Sheets Export Demo — visual mockup of the Google Sheets ROI flow.
 * Headline metric is now percentage (universal across lawn size + region).
 * Adds a visible decision-logic panel so prospects can SEE how skips fire.
 * Route: /demo/sheets-export
 */

// Daily log — events grouped by date with a "rollup" for uneventful days.
// Status decisions follow the rules engine card displayed up top.
const DAILY_LOG_GROUPED = [
  {
    date: '5/13', label: 'Glow-up day', highlight: false,
    rows: [
      ['Zone 2',   '49.0', '0',  'NO',  'SKIP',  'Manual hold — fungicide spray',      'Manual override', 'AMS + Blade'],
      ['Zone 4/5', '68.0', '0',  'NO',  'SKIP',  'Above MAD threshold',                'Auto-skipped',    ''],
      ['Zone 3',   '68.0', '0',  'NO',  'SKIP',  'Above MAD threshold',                'Auto-skipped',    ''],
    ],
  },
  {
    date: '5/14', label: 'Rachio native override · over-water incident', highlight: 'fired',
    rows: [
      ['Zone 2',   '51.0', '14', 'NO',  'FIRED', 'Rachio native override (pre-redeploy)', 'Watered', 'Flushed AMS/Blade'],
      ['Zone 4/5', '68.0', '8',  'NO',  'FIRED', 'Rachio native override',                'Watered', 'Already above MAD'],
      ['Zone 3',   '67.0', '0',  'NO',  'SKIP',  'Above MAD threshold',                   'Auto-skipped', ''],
    ],
  },
  // Two uneventful days collapse into one rollup
  { date: '5/15', rollup: 'All 3 zones SKIPPED — moisture above MAD threshold (no rain, no action)' },
  { date: '5/16', rollup: 'All 3 zones SKIPPED — 5-day forecast wet, preserving cycle' },
  {
    date: '5/17', label: 'Rain event 0.18"', highlight: 'rain',
    rows: [
      ['Zone 2',   '60.0', '0',  'YES', 'SKIP',  'Rain 0.18" — postponing',  'Auto-skipped', 'Open-Meteo'],
      ['Zone 4/5', '68.0', '0',  'YES', 'SKIP',  'Rain 0.18" — postponing',  'Auto-skipped', ''],
      ['Zone 3',   '70.0', '0',  'YES', 'SKIP',  'Rain 0.18" — postponing',  'Auto-skipped', ''],
    ],
  },
  {
    date: '5/18', label: 'Today · smart fire on Zone 2', highlight: 'fired',
    rows: [
      ['Zone 2',   '49.0', '8',  'NO',  'FIRED', 'Below MAD — cycle-and-soak triggered', 'Watered', 'EC 215 (ideal)'],
      ['Zone 4/5', '62.0', '0',  'NO',  'SKIP',  'Above MAD threshold',                  'Auto-skipped', ''],
      ['Zone 3',   '65.0', '0',  'NO',  'SKIP',  'Above MAD threshold',                  'Auto-skipped', ''],
    ],
  },
];

const ZONE_SUMMARY = [
  ['Zone',                  'Waterings', 'Skipped', 'Avg Moisture', 'Total Min', 'Last Moisture', 'Status', 'Recommendation', 'Alert'],
  ['Zone 2 (CH4 · WH52)',   '2',         '4',       '54.5',         '22',        '49.0',          'WATCH',  'WATER',          'DROP ALERT'],
  ['Zone 4/5 (CH5 · WH52)', '1',         '5',       '65.3',         '8',         '62.0',          'GOOD',   'SKIP',           ''],
  ['Zone 3 (CH3 · WH51)',   '0',         '6',       '66.5',         '0',         '65.0',          'GOOD',   'SKIP',           ''],
];

const SAVINGS = [
  ['Metric',                            'Value',         'Math'],
  ['Default Rachio baseline runtime',   '234 min',       '3 zones × 6 days × 13 min preset'],
  ['Actual TurfPulse runtime',          '30 min',        '14+8 (5/14) + 8 (5/18)'],
  ['',                                  '',              ''],
  ['🔥 Water reduction vs default',     '87%',           '1 − (30 ÷ 234)'],
  ['Skip rate',                         '89%',           '16 of 18 fire decisions were SKIP'],
  ['Annualized projection',             '~85–90%',       'Holds across season (rain skips offset peak heat)'],
];

const DECISION_RULES = [
  { id: 'manual',   icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400',  title: 'Manual override',            desc: 'Founder flagged a spray/treatment hold — skip regardless of other signals.' },
  { id: 'rain',     icon: CloudRain,     color: 'text-sky-600 dark:text-sky-400',     title: 'Rain event ≥ 0.10"',         desc: 'Open-Meteo confirmed rain in last 24h or forecast within 12h → SKIP.' },
  { id: 'above',    icon: CheckCircle,   color: 'text-emerald-600 dark:text-emerald-400', title: 'Moisture above MAD (50%)',   desc: 'Sensor reading at root depth above threshold → SKIP. Lawn has runway.' },
  { id: 'forecast', icon: CloudRain,     color: 'text-sky-600 dark:text-sky-400',     title: '5-day forecast wet',         desc: 'Cumulative forecast > 0.5" in next 5 days → preserve cycle, SKIP.' },
  { id: 'fire',     icon: Droplet,       color: 'text-rose-600 dark:text-rose-400',   title: 'Below MAD + no rain coming', desc: 'Only when all skip conditions fail → cycle-and-soak runtime calculated from nozzle rate × deficit.' },
];

const SheetsExportDemo = () => {
  const [tab, setTab] = useState('daily');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="sheets-demo-page">
      <SEO title="Live Google Sheets Demo · TurfPulse" description="See how TurfPulse data auto-flows into a Google Sheet — 87% water savings, live, with full decision logic." />

      {/* Hero — % savings is the lead metric */}
      <header className="max-w-6xl mx-auto px-4 py-10 text-center">
        <div className="inline-block text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold mb-2">Live Demo · Auto-Refreshing Google Sheet</div>
        <h1 className="text-3xl sm:text-5xl font-bold mb-3">87% less water<br className="sm:hidden"/> <span className="text-emerald-600 dark:text-emerald-400">vs default smart controllers</span></h1>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto">
          The Google Sheet below refreshes hourly from soil sensors in a real Round Rock yard.
          Every fire decision is logged with the agronomic reason. You see exactly where the savings come from.
        </p>
      </header>

      {/* 3-step diagram */}
      <section className="max-w-5xl mx-auto px-4 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: '1', title: 'Mint a token', body: '/admin/exports → click Mint. Get a URL with =IMPORTDATA() ready to paste.' },
            { n: '2', title: 'Paste in Google Sheets', body: 'New Sheet → 2 tabs (Daily Log + Zone Summary) → paste each formula in A1 of its tab.' },
            { n: '3', title: 'Add ROI math', body: 'Build a 3rd tab "Savings" with simple formulas. Share the Sheet URL anywhere.' },
          ].map(step => (
            <Card key={step.n} className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 leading-none">{step.n}</div>
                <div>
                  <div className="font-bold text-sm mb-1">{step.title}</div>
                  <div className="text-xs text-muted-foreground">{step.body}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Decision Logic — NEW: show how skips actually fire */}
      <section className="max-w-5xl mx-auto px-4 mb-8" data-testid="decision-logic-section">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-base">🧠 Decision Logic — how every SKIP / FIRE call is made</h2>
            <span className="text-[10px] text-muted-foreground">Rules engine · evaluated per zone</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Evaluated top-down. First rule that matches wins. Result logged with the matching reason on every row of the Daily Log.</p>
          <div className="space-y-2">
            {DECISION_RULES.map((rule, idx) => {
              const Icon = rule.icon;
              return (
                <div key={rule.id} className="flex items-center gap-3 p-2 rounded bg-muted/40" data-testid={`rule-${rule.id}`}>
                  <div className="text-[10px] font-bold text-muted-foreground w-6">{idx + 1}.</div>
                  <Icon size={18} className={`shrink-0 ${rule.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs">{rule.title}</div>
                    <div className="text-xs text-muted-foreground">{rule.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </section>

      {/* The mock Sheet */}
      <section className="max-w-6xl mx-auto px-4 mb-8">
        <div className="bg-white dark:bg-slate-900 rounded-t-lg shadow-md border border-slate-200 dark:border-slate-800">
          <div className="border-b border-slate-200 dark:border-slate-800 px-4 py-2 flex items-center gap-3 bg-slate-100 dark:bg-slate-800/40">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold">TurfPulse — Customer ROI</span>
            <span className="text-[10px] text-muted-foreground ml-auto">Auto-refresh: 1h · Synced 2 min ago</span>
          </div>

          {/* Tab content */}
          <div className="overflow-x-auto" data-testid="sheet-grid">
            {tab === 'daily' && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-bold border-b border-slate-200 dark:border-slate-700">
                    <th className="px-2 py-2 text-right text-[10px] font-mono text-slate-400 w-8">#</th>
                    {['Date', 'Zone', 'Moisture', 'Run (min)', 'Rain', 'Decision', 'Reason', 'Action', 'Notes'].map(h => (
                      <th key={h} className="px-3 py-2 text-left whitespace-nowrap border-r border-slate-100 dark:border-slate-800">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAILY_LOG_GROUPED.flatMap((group, gi) => {
                    if (group.rollup) {
                      return [(
                        <tr key={`rollup-${gi}`} className="bg-slate-50 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800 italic" data-testid={`rollup-row-${gi}`}>
                          <td className="px-2 py-1.5 bg-slate-100 dark:bg-slate-800/50 text-slate-400 text-[10px] font-mono text-right border-r border-slate-200 dark:border-slate-700">·</td>
                          <td className="px-3 py-1.5 font-mono text-xs">{group.date}</td>
                          <td colSpan={8} className="px-3 py-1.5 text-muted-foreground text-xs">
                            <span className="inline-block bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded text-[10px] font-bold mr-2">ROLLUP</span>
                            {group.rollup}
                          </td>
                        </tr>
                      )];
                    }
                    const out = [];
                    // Date label row (sub-header for the day)
                    if (group.label) {
                      out.push(
                        <tr key={`label-${gi}`} className="bg-amber-50/30 dark:bg-amber-950/10 border-b border-slate-100 dark:border-slate-800" data-testid={`day-label-${gi}`}>
                          <td className="px-2 py-1 bg-slate-100 dark:bg-slate-800/50 text-slate-400 text-[10px] font-mono text-right border-r border-slate-200 dark:border-slate-700">·</td>
                          <td colSpan={9} className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                            {group.date} — {group.label}
                          </td>
                        </tr>
                      );
                    }
                    group.rows.forEach((row, ri) => {
                      const [zone, moisture, runtime, rain, decision, reason, action, notes] = row;
                      const isFired = decision === 'FIRED';
                      out.push(
                        <tr key={`${gi}-${ri}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="px-2 py-1.5 bg-slate-50 dark:bg-slate-800/30 text-slate-400 text-[10px] font-mono text-right border-r border-slate-200 dark:border-slate-700">{ri + 1}</td>
                          <td className="px-3 py-1.5 font-mono">{group.date}</td>
                          <td className="px-3 py-1.5">{zone}</td>
                          <td className="px-3 py-1.5 font-mono">{moisture}</td>
                          <td className="px-3 py-1.5 font-mono">{runtime}</td>
                          <td className="px-3 py-1.5">{rain === 'YES' ? <span className="text-sky-600 dark:text-sky-400">YES</span> : 'NO'}</td>
                          <td className={`px-3 py-1.5 font-bold ${isFired ? 'text-rose-700 dark:text-rose-300 bg-rose-50/50 dark:bg-rose-950/30' : 'text-emerald-700 dark:text-emerald-300 bg-emerald-50/30 dark:bg-emerald-950/20'}`}>{decision}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{reason}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{action}</td>
                          <td className="px-3 py-1.5 text-[10px] text-muted-foreground italic">{notes}</td>
                        </tr>
                      );
                    });
                    return out;
                  })}
                </tbody>
              </table>
            )}

            {tab === 'summary' && (
              <table className="w-full text-xs">
                <tbody>
                  {ZONE_SUMMARY.map((row, ri) => (
                    <tr key={ri} className={`border-b border-slate-100 dark:border-slate-800 ${ri === 0 ? 'bg-emerald-50 dark:bg-emerald-950/40 font-bold text-emerald-800 dark:text-emerald-300' : ''}`}>
                      <td className="px-2 py-1.5 bg-slate-50 dark:bg-slate-800/30 text-slate-400 text-[10px] font-mono text-right border-r border-slate-200 dark:border-slate-700 w-8">{ri + 1}</td>
                      {row.map((cell, ci) => {
                        let cls = 'px-3 py-1.5 border-r border-slate-100 dark:border-slate-800';
                        if (ri > 0 && ci === 6 && cell === 'WATCH')  cls += ' bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-semibold';
                        if (ri > 0 && ci === 6 && cell === 'GOOD')   cls += ' bg-emerald-50/40 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 font-semibold';
                        if (ri > 0 && ci === 8 && cell === 'DROP ALERT') cls += ' bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 font-semibold';
                        return <td key={ci} className={cls}>{cell}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {tab === 'savings' && (
              <div className="p-6 space-y-5">
                {/* Headline % KPI tiles — universal, lawn-size agnostic */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="savings-kpi-row">
                  <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30 p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-bold">Water Reduction</div>
                    <div className="text-4xl font-bold text-emerald-700 dark:text-emerald-300 leading-tight my-1">87%</div>
                    <div className="text-[10px] text-muted-foreground">vs Rachio default preset</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Skip Rate</div>
                    <div className="text-4xl font-bold text-slate-800 dark:text-slate-200 leading-tight my-1">89%</div>
                    <div className="text-[10px] text-muted-foreground">16 of 18 decisions skipped</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Annualized</div>
                    <div className="text-4xl font-bold text-slate-800 dark:text-slate-200 leading-tight my-1">~88%</div>
                    <div className="text-[10px] text-muted-foreground">projected across season</div>
                  </div>
                </div>

                <table className="w-full text-xs">
                  <tbody>
                    {SAVINGS.map((row, ri) => (
                      <tr key={ri} className={`border-b border-slate-100 dark:border-slate-800 ${ri === 0 ? 'bg-emerald-50 dark:bg-emerald-950/40 font-bold text-emerald-800 dark:text-emerald-300' : ''} ${row[0]?.startsWith('🔥') ? 'bg-emerald-100/50 dark:bg-emerald-950/30' : ''}`}>
                        <td className={`px-2 py-1.5 bg-slate-50 dark:bg-slate-800/30 text-slate-400 text-[10px] font-mono text-right border-r border-slate-200 dark:border-slate-700 w-8`}>{row[0] ? ri + 1 : ''}</td>
                        {row.map((cell, ci) => {
                          let cls = 'px-3 py-1.5 border-r border-slate-100 dark:border-slate-800';
                          if (ri > 0 && ci === 1 && row[0]?.startsWith('🔥')) cls += ' text-emerald-700 dark:text-emerald-300 font-bold text-base';
                          if (ri > 0 && ci === 1 && !row[0]?.startsWith('🔥')) cls += ' font-mono';
                          return <td key={ci} className={cls}>{cell}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[10px] text-muted-foreground italic pt-2">
                  Percent savings are scale-invariant — they hold whether the lawn is 1,000 sqft or 10,000 sqft. We lead with % because it&apos;s the only number that travels across yards, regions, and seasons without needing to re-state assumptions.
                </div>
              </div>
            )}
          </div>

          {/* Tab bar */}
          <div className="border-t border-slate-200 dark:border-slate-800 px-2 py-2 flex items-center gap-1 bg-slate-50 dark:bg-slate-800/30">
            {[
              { id: 'daily',   label: 'Daily Log' },
              { id: 'summary', label: 'Zone Summary' },
              { id: 'savings', label: '💰 Savings' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`text-xs px-3 py-1.5 rounded ${tab === t.id ? 'bg-white dark:bg-slate-900 font-bold shadow-sm border-t-2 border-emerald-500' : 'text-muted-foreground hover:bg-white/60 dark:hover:bg-slate-800'}`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Customer pitch — now leads with % */}
      <section className="max-w-4xl mx-auto px-4 mb-8">
        <Card className="border-emerald-500 border-2 bg-emerald-50/40 dark:bg-emerald-950/20">
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400 font-bold mb-1">📣 What the Sheet tells your customer</div>
            <h2 className="text-3xl font-bold mb-3 text-emerald-700 dark:text-emerald-300">87% less water · 16 of 18 decisions were SKIP</h2>
            <p className="text-sm text-muted-foreground mb-3">
              In the last 6 days, TurfPulse made <strong>18 fire decisions across 3 zones</strong>. It skipped 16 of them — only firing when the soil actually needed water. That&apos;s <strong>87% less pump time than a default Rachio schedule would have used.</strong>
            </p>
            <p className="text-sm text-muted-foreground italic">
              We lead with <strong>%</strong> because it&apos;s the universal proof — it stays true across any lawn size, climate zone, or water rate. No assumptions needed.
            </p>
          </div>
        </Card>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 pb-16 text-center">
        <h3 className="text-xl font-bold mb-2">Build this Sheet for your lawn in 60 seconds</h3>
        <p className="text-sm text-muted-foreground mb-4">Mint a token. Paste 2 formulas. Done.</p>
        <a
          href="/admin/exports"
          className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-md text-sm transition"
          data-testid="cta-mint-token"
        >
          Go to /admin/exports → Mint Token
        </a>
      </section>
    </div>
  );
};

export default SheetsExportDemo;
